// The visit-note endpoints, in one place.
//
// Instance N owns the routes (backend/app/routes/notes.py) and the shapes; this
// module is the frontend's only knowledge of them. Both calls are practice- and
// claim-scoped, both are PHI-audited server-side, and both are gated on the
// `note_viewer` feature.
//
// Nothing here writes a code onto a claim. The one write is the per-claim
// "note reviewed" flag, which is a person's own statement about their own
// reading — it moves an advisory readiness row and nothing else.

const API_URL = process.env.REACT_APP_API_URL || "";

async function readError(response, fallback) {
  try {
    const body = await response.json();
    return (body && body.error) || fallback;
  } catch (err) {
    return fallback;
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const error = new Error(await readError(response, fallback));
    error.status = response.status;
    throw error;
  }
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    throw error;
  }
  return body;
}

const base = (practiceId, claimId) =>
  `${API_URL}/api/practices/${encodeURIComponent(practiceId)}/claims/${encodeURIComponent(claimId)}`;

export const DOC_TYPE_LABELS = {
  progress_note: "Progress note",
  plan_of_care: "Plan of care (485)",
  oasis: "OASIS",
  face_to_face: "Face-to-face",
  order: "Order",
  other: "Document",
};

export const SOURCE_LABELS = {
  cobalt: "eCW",
  tebra: "Tebra",
  practicefusion: "Practice Fusion",
  kinnser: "WellSky",
  demo: "Demo",
};

const str = (value) => (typeof value === "string" && value ? value : null);

// ICD-10-CM as the CLAIM carries it: three characters, then the rest.
// `coding_assist_service` stores a diagnosis dotless (`F411`) and
// `PracticeClaim.icd_codes` holds it dotted (`F41.1`), so comparing the two
// spellings is how a diagnosis already on the claim got offered again with a
// live Accept. Dotted is canonical (Jordan), because the claim is what a code
// has to match.
//
// CALLERS MUST PASS A DIAGNOSIS. This is a blind string transform: give it
// `99214` and it returns `992.14`. The guard cannot be inferred in here, because
// shape does not tell the kinds apart — `G2212` is a HCPCS procedure and
// `G22.12` is a plausible ICD-10 code, and neither the letter prefix nor the
// length separates them. Which kind a code is, is the CALLER's knowledge, so
// every call site carries the exclusion: `normalizeLine` on `prefix === "dx"`,
// `canonical()` on `line.isProcedure`, and `onClaim`, whose input is the claim's
// diagnosis list and so is diagnoses by definition. Stated here so that is a
// documented constraint rather than three places remembering.
export function dottedIcd(code) {
  const bare = code.replace(/\./g, "");
  return bare.length > 3 ? `${bare.slice(0, 3)}.${bare.slice(3)}` : bare;
}

function normalizeDiagnosis(raw, index) {
  const d = raw || {};
  return {
    order: Number(d.order) || index + 1,
    code: str(d.code) || "",
    description: str(d.description) || "",
  };
}

export function normalizeNote(raw, index = 0) {
  const n = raw || {};
  const docType = str(n.doc_type) || "progress_note";
  return {
    // Identity for the tab strip and for "which document am I reading". A row
    // always has an id, but this is the only thing keeping tab switching from
    // silently doing nothing if one ever arrives without — every tab would key
    // on null and every lookup would answer with the first note.
    id: n.id ?? `${str(n.source) || "?"}|${str(n.external_id) || "?"}|${index}`,
    source: str(n.source) || "",
    docType,
    docLabel: DOC_TYPE_LABELS[docType] || DOC_TYPE_LABELS.other,
    sourceLabel: SOURCE_LABELS[str(n.source)] || str(n.source) || "",
    episodeStart: str(n.episode_start),
    externalId: str(n.external_id),
    title: str(n.title),
    author: str(n.author),
    signedAt: str(n.signed_at),
    // The server's own verdict, not ours. Today it is derived from `signed_at`,
    // so a source that never reports signing reads as not signed — which is
    // "Atlas was not told", not "the provider did not sign". The viewer says it
    // that way round. Reading the field rather than re-deriving it means that
    // when the server learns to tell them apart, the viewer follows for free.
    isSigned: n.is_signed === true,
    // Note length, served so the viewer never has to measure body_text. A very
    // short note is a stub, and that is worth a coder seeing before they read.
    characters: Number.isFinite(Number(n.characters)) ? Number(n.characters) : null,
    serviceDate: str(n.service_date),
    // The note itself. Plain text: the server strips HTML at ingest and the
    // viewer renders React text nodes, so nothing from an EMR is ever markup.
    bodyText: typeof n.body_text === "string" ? n.body_text : "",
    diagnoses: Array.isArray(n.diagnoses)
      ? n.diagnoses.map(normalizeDiagnosis)
      : [],
    fetchedAt: str(n.fetched_at),
  };
}

function normalizeReview(body) {
  return {
    reviewed: body.note_reviewed === true,
    reviewedBy: str(body.note_reviewed_by),
    reviewedAt: str(body.note_reviewed_at),
  };
}

/** GET …/claims/<id>/notes — every note the coder should read for this claim. */
export async function fetchClaimNotes(practiceId, claimId) {
  const body = await request(
    `${base(practiceId, claimId)}/notes`,
    {},
    "Could not load the notes for this claim.",
  );
  if (!Array.isArray(body.notes)) {
    throw new Error(
      "The response could not be read, so the notes may be incomplete. Try again.",
    );
  }
  return {
    // The order the server sent, untouched. `visit_note_service._in_reading_order`
    // is the one authority on it: home-health documents in doc-type order, and
    // within a type the newest fetch first so an amended note opens on its
    // CURRENT version. Re-sorting here was a second ordering rule that disagreed
    // with that one — it opened the oldest of two notes on a visit, which is the
    // superseded one — and the first tab is the tab that opens.
    notes: body.notes.map((n, i) => normalizeNote(n, i)),
    review: normalizeReview(body),
    // What a coding suggestion for this practice costs, as the server words it.
    // Needed here rather than on the suggest response because it has to be on
    // screen before the click that spends it.
    assistCost: str(body.assist_cost),
    // The suggestion this claim already has, with what the biller decided about
    // it. Null when nobody has asked. This is the response that draws the panel,
    // so it is where the panel's state comes back from after a reload — the
    // alternative was pressing the button that spends, to see something Atlas
    // already had.
    suggestion: body.suggestion ? normalizeSuggestion(body.suggestion) : null,
  };
}

// ---- coding assist ----
//
// A suggestion NEVER touches a claim. `requestSuggestion` writes a
// coding_suggestions row and returns what the model proposed;
// `recordDecision` writes what the person decided. Codes reach the claim only
// through the editor's own edit path, from a person's click on Accept, so the
// claim's edit log names the person and not the model.

const num = (value) => {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n) ? null : n;
};

// The service stores the suggestion as JSON in a text column, so it may arrive
// parsed or as a string depending on how the route hands it back.
function readSuggestionBody(raw) {
  if (raw && typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (err) {
      return {};
    }
  }
  return {};
}

// A line with no code is not a suggestion, it is noise: the service is supposed
// to drop any line whose quote or code did not validate, and anything that slips
// through would render as an Accept button that puts an empty code on a claim.
function normalizeLine(raw, prefix, index) {
  const l = raw || {};
  const raw_code = (str(l.code) || "").trim().toUpperCase();
  if (!raw_code) return null;
  // Diagnoses only — see dottedIcd on why the kind cannot be inferred there.
  const code = prefix === "dx" ? dottedIcd(raw_code) : raw_code;
  const confidence = num(l.confidence);
  return {
    key: `${prefix}-${index}`,
    code,
    description: str(l.description) || "",
    // The server's own caveat on a line that PASSED validation — e.g. a code
    // that is a heading in the ICD-10-CM year Atlas ships, for a date of service
    // that year cannot speak to. `check_icd10` produces it and the scrubber
    // preserves it on purpose; dropping it offered the code with a clean Accept
    // and the caveat never reached the biller. (`reason`/`rationale` used to be
    // read here and are in neither the prompt's output shape nor the validator —
    // a third reader of a field nobody sends.)
    warning: str(l.warning) || "",
    quote: str(l.quote) || "",
    confidence,
    modifiers: Array.isArray(l.modifiers)
      ? l.modifiers.map((m) => String(m).trim().toUpperCase()).filter(Boolean)
      : [],
    units: num(l.units) || 1,
  };
}

// What the biller already decided about this suggestion, keyed the way the panel
// keys it: `decision_detail.lines` back into `{key: {decision, code, ...}}`.
//
// THE ROW HAS CARRIED THIS SINCE IT WAS ADDED and nothing read it, so a reload
// re-rendered every decided line as undecided and invited the biller to decide it
// again — over a `decision_detail` that already said otherwise. The panel writes
// this shape in `record()`; this is the same shape read back, so the round trip is
// the panel's own and not a second interpretation of it.
//
// A line with no key, or no known decision, is dropped rather than guessed at: a
// position is not an identity across posts (`routes/notes.py` says the same on the
// way in), and a decision the panel cannot render is one it must not invent.
const DECISIONS = ["accepted", "edited", "rejected"];

function normalizeDecisions(raw) {
  const detail = raw && typeof raw === "object" ? raw : {};
  const lines = Array.isArray(detail.lines) ? detail.lines : [];
  const out = {};
  lines.forEach((line) => {
    if (!line || typeof line !== "object") return;
    const key = typeof line.key === "string" || typeof line.key === "number"
      ? String(line.key)
      : null;
    if (key === null || !DECISIONS.includes(line.decision)) return;
    const code = (str(line.code) || "").trim().toUpperCase();
    // Rejected is the one decision that needs no code, and a decided line with no
    // code would otherwise render an Accept for an empty string.
    if (!code && line.decision !== "rejected") return;
    out[key] = {
      decision: line.decision,
      code,
      modifiers: Array.isArray(line.modifiers)
        ? line.modifiers.map((m) => String(m).trim().toUpperCase()).filter(Boolean)
        : [],
      units: num(line.units) || 1,
    };
  });
  return out;
}

export function normalizeSuggestion(raw) {
  const s = raw || {};
  const body = readSuggestionBody(s.suggestion);
  const lines = (list, prefix) =>
    (Array.isArray(list) ? list : [])
      .map((line, i) => normalizeLine(line, prefix, i))
      .filter(Boolean);
  return {
    id: s.id ?? null,
    model: str(s.model),
    promptVersion: str(s.prompt_version),
    createdAt: str(s.created_at),
    requestedBy: str(s.requested_by),
    visitNoteId: s.visit_note_id ?? null,
    diagnoses: lines(body.diagnoses, "dx"),
    procedures: lines(body.procedures, "cpt"),
    flags: (Array.isArray(body.flags) ? body.flags : []).filter(
      (f) => typeof f === "string" && f,
    ),
    // The stored verdict, whole and per line. `decision` is the server's
    // recompute over every line it knows about, not the aggregate any one post
    // sent.
    decision: DECISIONS.includes(s.decision) ? s.decision : null,
    decidedBy: str(s.decided_by),
    decidedAt: str(s.decided_at),
    decisions: normalizeDecisions(s.decision_detail),
  };
}

/** POST …/claims/<id>/suggest — one person, one claim, one charged call.
 *
 * SENDS NO NOTE ID. `coding_assist_service.note_to_code_from` chooses, and it is
 * the one authority: the suggest route codes that note and the notes GET prices
 * that same note, so a biller cannot be quoted for one and charged for another.
 * This used to post `visit_note_id`, which the route has never read — the panel
 * then checked the answer against the tab it was sent from and blanked when they
 * differed, which `note_to_code_from`'s own docstring calls normal (the first tab
 * can be an unsigned draft or a plan of care). One paid call, nothing rendered,
 * no error, and cached on input_hash so every later press returned the same blank.
 */
export async function requestSuggestion(practiceId, claimId) {
  const body = await request(
    `${base(practiceId, claimId)}/suggest`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    "Could not suggest codes from this note.",
  );
  // `{"suggestion": row.to_dict()}` — and `to_dict()` has its own "suggestion"
  // key inside it. The envelope comes off here; normalizeSuggestion is handed
  // the ROW. Passing the envelope cost every line and, because `id` came back
  // undefined, every decision: `recordDecision` bails on a null id, so nothing
  // a biller accepted, edited or rejected was ever written down.
  return normalizeSuggestion(body.suggestion);
}

/** POST …/suggestions/<id>/decide — who decided what, for the audit.
 *
 * Returns the refreshed row, normalised. The decide route answers with the same
 * `{"suggestion": row.to_dict()}` envelope the suggest route uses, and this used
 * to hand back the raw envelope while `requestSuggestion` handed back the row —
 * harmless only because nothing reads it, which is round one's bug re-armed for
 * whoever reads it next.
 */
export async function recordDecision(practiceId, suggestionId, decision, lines) {
  const body = await request(
    `${API_URL}/api/practices/${encodeURIComponent(practiceId)}/suggestions/${encodeURIComponent(suggestionId)}/decide`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ decision, lines }),
    },
    "Could not record that decision.",
  );
  return normalizeSuggestion(body.suggestion);
}

/** POST …/claims/<id>/note-reviewed — the person's own "I have read it". */
export async function setNoteReviewed(practiceId, claimId, reviewed) {
  const body = await request(
    `${base(practiceId, claimId)}/note-reviewed`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reviewed: !!reviewed }),
    },
    "Could not save that you have reviewed the note.",
  );
  return normalizeReview(body);
}
